import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import createBrowserHistory from 'history/createBrowserHistory';
import createReduxWaitForMiddleware from 'redux-wait-for-action';
import createSagaMiddleware from 'redux-saga';
import storage from 'redux-persist/lib/storage';
import { applyMiddleware, compose, createStore } from 'redux';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { createLogger } from 'redux-logger';
import { persistStore, persistReducer } from 'redux-persist';

import { APP_PERSIST_ID } from 'constants/store';
import { NODE_ENV } from 'constants/env';

import rootReducer from './reducers';
import rootSaga from './sagas';

/**
 * The config for the redux persist.
 *
 * @type {Object}
 */
const reduxPersistConfig = {
    // We don't want to persist the following keys.
    blacklist      : ['hellosign'],
    // This is good for development environments.
    debug          : NODE_ENV === 'development',
    // A key to keep in `localStorage`.
    key            : APP_PERSIST_ID,
    // Extraneous options for persisting and auto merging the previous and next states.
    stateReconciler: autoMergeLevel2,
    // The type of storage to use.
    storage
};

/**
 * Create the browser history.
 * This is the HTML5 history, and not hash history.
 * Pretty URLs.
 *
 * @type {Object}
 */
const history = createBrowserHistory();

/**
 * Configure the store for the application.
 *
 * @method configureStore
 * @return {Object}       The store, history, and persistor
 */
const configureStore = () => {
    // Combine the main reducers with the redux-persist.
    const reducers = persistReducer(reduxPersistConfig, rootReducer);
    // Connect the react-router to the redux reducers.
    const connectedReducers = connectRouter(history)(reducers);
    // Create the redux-saga middleware.
    const sagaMiddleware = createSagaMiddleware();
    const reduxWaitForMiddleware = createReduxWaitForMiddleware();
    // Build the redux-logger (good for debugging redux actions).
    const logger = createLogger({
        duration: true
    });
    // Build the middlewares list for the store.
    let middlewaresList = [routerMiddleware(history), sagaMiddleware, reduxWaitForMiddleware];
    // If we're on the development environment then let's add the redux-logger.
    if (NODE_ENV === 'development') {
        // NOTE: redux-logger must be the last middleware in the list,
        // otherwise it will log all of the other middlewares + promises before it.
        // @link https://github.com/evgenyrodionov/redux-logger/issues/20
        middlewaresList = [...middlewaresList, logger];
    }
    // Run the Redux DevTools Extension
    // NOTE: Usually you would only run the redux-devtools in 'development' environment,
    // but it's good to have it available if the extension is present.
    const composeEnhancers =
        NODE_ENV === 'development'
            ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
            : compose;
    // Create the store.
    const store = createStore(
        connectedReducers,
        composeEnhancers(applyMiddleware(...middlewaresList))
    );
    // Create the redux-persist items.
    const persistor = persistStore(store);
    // Run the root redux-saga.
    sagaMiddleware.run(rootSaga);
    // Return an object consisting of the store, the history, and the persistor.
    // This is the object return for redux-persist to bind correctly.
    return {
        history,
        persistor,
        store
    };
};

export default configureStore;
